import { Loader, Stack, Text } from '@scality/core-ui';
import { Button } from '@scality/core-ui/dist/next';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import styled from 'styled-components';
import { isInCustomerPortal, userManager } from '../Auth';
import { IframeLoginPage } from '../components/IframeLoginPage';
import { XXLargeGradientText } from '../components/TextVariants';
import { useAuth } from '../hooks/useAuth';
import { useMcpBridge } from '../mcp/McpBridgeProvider';

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 16px;
`;

/**
 * An embedded Guardian cannot redirect to OneLogin, but the Customer Portal has already signed the
 * user in to OneLogin, so a hidden-iframe `prompt=none` sign-in can reuse that session.
 * Browsers that block third-party cookies refuse it, and the caller then shows the sign-in button.
 * Returns whether that one attempt is still running.
 */
const useSilentSignIn = (enabled: boolean) => {
  const [settled, setSettled] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!enabled || started.current) return;
    started.current = true;
    userManager
      .signinSilent()
      // A refusal (login_required, timeout) is expected, not an error: the sign-in button takes over.
      .catch(() => {})
      .finally(() => setSettled(true));
  }, [enabled]);

  return enabled && !settled;
};

/** Gates the whole app on auth; renders the routed modes only once authenticated. */
export const AuthGate: React.FC = () => {
  const auth = useAuth();
  const { isInIframe } = useMcpBridge();
  const { search } = useLocation();
  const signingInSilently = useSilentSignIn(
    isInCustomerPortal(search, isInIframe) && !auth.isLoading && !auth.isAuthenticated,
  );

  useEffect(() => {
    if (isInIframe) {
      document.body.classList.add('iframe-mode');
      return () => document.body.classList.remove('iframe-mode');
    }
  }, [isInIframe]);

  if (auth.isLoading || signingInSilently) {
    return (
      <WelcomeContainer>
        <Loader size="massive" />
      </WelcomeContainer>
    );
  }

  if (isInIframe && !auth.isAuthenticated) {
    return <IframeLoginPage />;
  }

  if (auth.isAuthenticated) {
    return <Outlet />;
  }

  return (
    <WelcomeContainer>
      <Stack direction="vertical">
        <XXLargeGradientText>Welcome!</XXLargeGradientText>
        <Text variant="Basic" color="textSecondary">
          Please sign in to continue.
        </Text>
        <Button onClick={auth.signIn} label="Sign in" variant="primary" />
      </Stack>
    </WelcomeContainer>
  );
};
